/**
 * useOrderUpdates Hook - Rider App
 * 
 * Manages real-time order updates for riders via WebSocket.
 * Subscribes to order-updates channel and refetches available/active orders.
 */

import { useEffect, useRef, useState } from 'react';
import { wsClient } from '@/lib/ws';
import { useAuthStore } from '@/store/auth';
import { logger } from '@/lib/logger';

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

interface OrderUpdateEvent {
  order_id: string;
  status?: string;
  rider_id?: string;
  [key: string]: any;
}

export function useOrderUpdates() {
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [lastUpdate, setLastUpdate] = useState<number>(0);
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const handlerRef = useRef<((data: OrderUpdateEvent) => void) | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setConnectionState('idle');
      return;
    }

    // Check if offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setConnectionState('disconnected');
      logger.info('Browser offline, skipping WebSocket connection');
      return;
    }

    // Connect to WebSocket
    setConnectionState('connecting');
    
    try {
      wsClient.connect();
      
      // Define event handler
      const handleOrderUpdate = (data: OrderUpdateEvent) => {
        logger.info('Order update received (rider)', { orderId: data.order_id, status: data.status });
        
        // Trigger a timestamp update so components can react
        setLastUpdate(Date.now());
      };

      // Store handler ref for cleanup
      handlerRef.current = handleOrderUpdate;

      // Subscribe to order updates channel
      wsClient.subscribe('order-updates', handleOrderUpdate);
      
      setConnectionState('connected');
      logger.info('Rider subscribed to order updates');

    } catch (error) {
      logger.error('Failed to connect to WebSocket', error as Error);
      setConnectionState('error');
    }

    // Cleanup on unmount or auth change
    return () => {
      if (handlerRef.current) {
        wsClient.unsubscribe('order-updates', handlerRef.current);
        logger.info('Rider unsubscribed from order updates');
        handlerRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      logger.info('Browser back online, reconnecting WebSocket');
      setConnectionState('connecting');
      wsClient.connect();
    };

    const handleOffline = () => {
      logger.info('Browser offline, disconnecting WebSocket');
      setConnectionState('disconnected');
      wsClient.disconnect();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, []);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    lastUpdate, // Components can watch this to trigger refetch
  };
}
